import React, { useMemo, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import { format } from 'date-fns';
import { parseApiTimestamp } from '../utils/dateUtils';

function quoteLabel(q) {
  if (!q) return '';
  return String(q.identifier || '').trim() || `Quote #${q.quote_number}` || `#${q.id}`;
}

function orderLabel(o) {
  if (!o) return '';
  return String(o.identifier || '').trim() || `Order #${o.work_order_number}` || `#${o.id}`;
}

function packingSlipLabel(s) {
  if (!s) return '';
  return String(s.identifier || '').trim() || String(s.packing_slip_number || '').trim() || `#${s.id}`;
}

function customerInvoiceLabelDoc(inv) {
  if (!inv) return '';
  return String(inv.identifier || '').trim() || String(inv.invoice_number || '').trim() || `#${inv.id}`;
}

function docCreatorName(row) {
  const n = [row?.created_by_first_name, row?.created_by_last_name].filter(Boolean).join(' ').trim();
  return n || '—';
}

function formatLineageWhen(v) {
  const d = parseApiTimestamp(v);
  if (!d) return '—';
  return format(d, 'MM/dd/yyyy · h:mm a');
}

function QuoteLineageMeta({ display }) {
  if (!display) return null;
  const lines = [];
  lines.push(
    <Typography key="created" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
      {formatLineageWhen(display.created_at)} · Created by {display.created_by_name}
    </Typography>
  );
  if (display.new_version) {
    lines.push(
      <Typography key="rev" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
        New version created by {display.new_version.by_name} · {formatLineageWhen(display.new_version.at)}
      </Typography>
    );
  }
  if (display.quote_split) {
    lines.push(
      <Typography key="split" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
        Quote split by {display.quote_split.by_name} · {formatLineageWhen(display.quote_split.at)}
        {display.quote_split.inferred ? ' · (from notes)' : ''}
      </Typography>
    );
  }
  if (display.referenced_order_quote) {
    lines.push(
      <Typography key="roq" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
        Referenced order quote created by {display.referenced_order_quote.by_name} ·{' '}
        {formatLineageWhen(display.referenced_order_quote.at)}
        {display.referenced_order_quote.inferred ? ' · (from notes)' : ''}
      </Typography>
    );
  }
  return <Box sx={{ mt: 0.25, maxWidth: 'min(100%, 720px)' }}>{lines}</Box>;
}

function OrderLineageMeta({ display }) {
  if (!display) return null;
  const lines = [];
  lines.push(
    <Typography key="oc" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
      {formatLineageWhen(display.created_at)} · Created by {display.created_by_name}
    </Typography>
  );
  if (display.referenced_order) {
    lines.push(
      <Typography key="ro" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
        Referenced order created by {display.referenced_order.by_name} ·{' '}
        {formatLineageWhen(display.referenced_order.at)}
        {display.referenced_order.inferred ? ' · (from notes)' : ''}
      </Typography>
    );
  } else if (display.order_from_quote) {
    lines.push(
      <Typography key="ofq" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
        Order created from quote by {display.order_from_quote.by_name} ·{' '}
        {formatLineageWhen(display.order_from_quote.at)}
        {display.order_from_quote.inferred ? ' · (from notes)' : ''}
      </Typography>
    );
  }
  if (display.copied_from_order) {
    const { parent_id, parent_label } = display.copied_from_order;
    lines.push(
      <Typography key="cf" variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
        Copied from{' '}
        <Link to={`/work-orders/${parent_id}`} style={{ color: 'inherit' }}>
          {parent_label}
        </Link>
      </Typography>
    );
  }
  return <Box sx={{ mt: 0.15, pl: 0, maxWidth: 'min(100%, 720px)' }}>{lines}</Box>;
}

function deletedByName(s) {
  const n = [s?.deleted_by_first_name, s?.deleted_by_last_name].filter(Boolean).join(' ').trim();
  return n || '—';
}

function OrderSlipsAndInvoicesBlock({ order }) {
  const slips = order.packing_slips || [];
  const invoices = order.customer_invoices || [];
  if (slips.length === 0 && invoices.length === 0) return null;
  return (
    <Box sx={{ mt: 0.35, pl: 0.25, maxWidth: 'min(100%, 720px)' }}>
      {slips.length > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.45 }}>
          Packing slips:{' '}
          {slips.map((s, i) => {
            const deleted = !!s.deleted;
            return (
              <Fragment key={`ps-${s.id}`}>
                {i > 0 ? '; ' : ''}
                <Box
                  component="span"
                  sx={
                    deleted
                      ? {
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                          opacity: 0.85
                        }
                      : undefined
                  }
                >
                  {deleted ? (
                    <span>{packingSlipLabel(s) || `Slip #${s.id}`}</span>
                  ) : (
                    <Link to={`/packing-slips/${s.id}`} style={{ color: 'inherit' }}>
                      {packingSlipLabel(s)}
                    </Link>
                  )}
                  <span>
                    {' '}
                    ({formatLineageWhen(s.created_at)} · {docCreatorName(s)}
                    {deleted
                      ? ` · deleted${s.deleted_at ? ` ${formatLineageWhen(s.deleted_at)}` : ''} · ${deletedByName(s)}`
                      : ''}
                    )
                  </span>
                  {deleted ? (
                    <Chip
                      size="small"
                      label="deleted"
                      sx={{ ml: 0.5, height: 18, fontSize: '0.6rem', verticalAlign: 'middle' }}
                    />
                  ) : null}
                </Box>
              </Fragment>
            );
          })}
        </Typography>
      ) : null}
      {invoices.length > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.45, mt: slips.length ? 0.2 : 0 }}>
          Invoices:{' '}
          {invoices.map((inv, i) => (
            <Fragment key={`ci-${inv.id}`}>
              {i > 0 ? '; ' : ''}
              <Link to={`/customer-invoices/${inv.id}`} style={{ color: 'inherit' }}>
                {customerInvoiceLabelDoc(inv)}
              </Link>
              <span>
                {' '}
                ({formatLineageWhen(inv.created_at)} · {docCreatorName(inv)})
              </span>
            </Fragment>
          ))}
        </Typography>
      ) : null}
    </Box>
  );
}

function OrderRowWithCopyTree({ order, anchorWorkOrderId, childOrdersByParentOrderId, showLeadArrow = true }) {
  const children = (childOrdersByParentOrderId.get(order.id) || []).slice().sort((a, b) =>
    orderLabel(a).localeCompare(orderLabel(b), undefined, { numeric: true })
  );
  return (
    <Box sx={{ pl: showLeadArrow ? 2 : 0, py: 0.35 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: order.id === anchorWorkOrderId ? 600 : 400 }}>
        {showLeadArrow ? '→ ' : ''}
        <Link to={`/work-orders/${order.id}`} style={{ color: 'inherit' }}>
          {orderLabel(order)}
        </Link>
        {String(order.status || '').toLowerCase() === 'cancelled' ? (
          <Chip size="small" label="cancelled" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />
        ) : null}
        {order.superseded_by_work_order_id ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            (superseded)
          </Typography>
        ) : null}
        {order.id === anchorWorkOrderId ? (
          <Chip size="small" label="this order" variant="outlined" sx={{ ml: 0.5, height: 20, fontSize: '0.65rem' }} />
        ) : null}
      </Typography>
      <OrderLineageMeta display={order.lineage_display} />
      <OrderSlipsAndInvoicesBlock order={order} />
      {children.length > 0 ? (
        <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 0.75, pl: 1.25, mt: 0.35 }}>
          {children.map((ch) => (
            <OrderRowWithCopyTree
              key={ch.id}
              order={ch}
              anchorWorkOrderId={anchorWorkOrderId}
              childOrdersByParentOrderId={childOrdersByParentOrderId}
              showLeadArrow
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function QuoteBranch({
  quote,
  childrenByParent,
  ordersByQuoteId,
  anchorQuoteId,
  anchorWorkOrderId,
  childOrdersByParentOrderId
}) {
  if (!quote) return null;
  const qid = quote.id;
  const children = childrenByParent.get(qid) || [];
  children.sort((a, b) => quoteLabel(a).localeCompare(quoteLabel(b), undefined, { numeric: true }));

  const orders = ordersByQuoteId.get(qid) || [];
  const st = String(quote.status || '').toLowerCase();
  const isDeprecated = st === 'deprecated';

  return (
    <Box sx={{ pl: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0, py: 0.25 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
          <Typography component="span" variant="body2" sx={{ fontWeight: qid === anchorQuoteId ? 700 : 400 }}>
            <Link to={`/quotes/${qid}`} style={{ color: 'inherit', textDecoration: qid === anchorQuoteId ? 'underline' : 'none' }}>
              {quoteLabel(quote)}
            </Link>
          </Typography>
          {isDeprecated ? <Chip size="small" label="deprecated" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} /> : null}
          {qid === anchorQuoteId ? (
            <Chip size="small" label="this quote" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          ) : null}
        </Box>
        <QuoteLineageMeta display={quote.lineage_display} />
      </Box>
      {orders.map((o) => (
        <OrderRowWithCopyTree
          key={`o-${o.id}`}
          order={o}
          anchorWorkOrderId={anchorWorkOrderId}
          childOrdersByParentOrderId={childOrdersByParentOrderId}
          showLeadArrow
        />
      ))}
      {children.length > 0 ? (
        <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 0.75, pl: 1.25, mt: 0.5 }}>
          {children.map((ch) => (
            <QuoteBranch
              key={ch.id}
              quote={ch}
              childrenByParent={childrenByParent}
              ordersByQuoteId={ordersByQuoteId}
              anchorQuoteId={anchorQuoteId}
              anchorWorkOrderId={anchorWorkOrderId}
              childOrdersByParentOrderId={childOrdersByParentOrderId}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * @param {{ loading?: boolean, error?: string, data?: object }} props
 */
export default function QuoteOrderLineagePanel({ loading, error, data }) {
  const built = useMemo(() => {
    if (!data) return null;
    const allOrders = data.orders || [];
    const orderIds = new Set(allOrders.map((o) => o.id));
    const childOrdersByParentOrderId = new Map();
    for (const o of allOrders) {
      const p = o.derived_from_work_order_id;
      if (p == null || !orderIds.has(p)) continue;
      if (!childOrdersByParentOrderId.has(p)) childOrdersByParentOrderId.set(p, []);
      childOrdersByParentOrderId.get(p).push(o);
    }
    const quotes = data.quotes || [];
    if (quotes.length === 0) {
      return { childOrdersByParentOrderId, allOrders, rootQuote: null, rootId: null, noQuoteLineage: true };
    }
    const quoteById = new Map(quotes.map((q) => [q.id, q]));
    const childrenByParent = new Map();
    for (const q of quotes) {
      const p = q.derived_from_quote_id;
      if (p == null) continue;
      if (!quoteById.has(p)) continue;
      if (!childrenByParent.has(p)) childrenByParent.set(p, []);
      childrenByParent.get(p).push(q);
    }
    const ordersByQuoteId = new Map();
    for (const o of allOrders) {
      const qid = o.quote_id;
      if (qid == null) continue;
      if (!ordersByQuoteId.has(qid)) ordersByQuoteId.set(qid, []);
      ordersByQuoteId.get(qid).push(o);
    }
    const rootId = data.root_quote_id;
    const rootQuote = rootId != null ? quoteById.get(rootId) : null;
    return {
      quoteById,
      childrenByParent,
      ordersByQuoteId,
      rootQuote,
      rootId,
      childOrdersByParentOrderId,
      allOrders,
      noQuoteLineage: false
    };
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading lineage…
        </Typography>
      </Box>
    );
  }
  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }
  if (!data) return null;

  if (!built?.rootQuote && (!data.orders || data.orders.length === 0)) {
    return null;
  }

  if (!built?.rootQuote) {
    const onlyOrders = built.allOrders || [];
    const roots = onlyOrders.filter(
      (o) => o.derived_from_work_order_id == null || !onlyOrders.some((x) => x.id === o.derived_from_work_order_id)
    );
    const singleStandalone =
      onlyOrders.length === 1 && onlyOrders[0].derived_from_work_order_id == null;
    return (
      <Box sx={{ py: 0.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
          Lineage
        </Typography>
        {singleStandalone ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            This order is not linked to a quote. No quote family to show.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Order copies branch under their source order. Dates and names come from each order record and activity log when
            available.
          </Typography>
        )}
        {singleStandalone ? (
          onlyOrders.map((o) => (
            <Box key={o.id} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <Link to={`/work-orders/${o.id}`}>{orderLabel(o)}</Link>
                {o.id === data.anchor_work_order_id ? ' (this order)' : ''}
              </Typography>
              <OrderLineageMeta display={o.lineage_display} />
              <OrderSlipsAndInvoicesBlock order={o} />
            </Box>
          ))
        ) : (
          roots.map((o) => (
            <OrderRowWithCopyTree
              key={o.id}
              order={o}
              anchorWorkOrderId={data.anchor_work_order_id}
              childOrdersByParentOrderId={built.childOrdersByParentOrderId}
              showLeadArrow={false}
            />
          ))
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
        Quote &amp; order lineage
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        Dates and names come from the quote or order record and activity log when available. Older rows may show “from notes” when
        the actor was not logged. A referenced order is an order created from a partial quote (subset of lines). Order copies
        (no quote link) nest under their source order.
      </Typography>
      <QuoteBranch
        quote={built.rootQuote}
        childrenByParent={built.childrenByParent}
        ordersByQuoteId={built.ordersByQuoteId}
        anchorQuoteId={data.anchor_quote_id}
        anchorWorkOrderId={data.anchor_work_order_id}
        childOrdersByParentOrderId={built.childOrdersByParentOrderId}
      />
    </Box>
  );
}
